import { GridFilterInputValue } from '@mui/x-data-grid-pro';

import { handleProcessRowUpdate } from './fetchSocket';
import { formatDateToDateString, parseDateToLocal } from './dateFormatter';

/**
 * Tells whether an edit commit actually changed a cell, across the shape
 * mismatch between server rows and grid commits: the server sends dates as
 * strings and blanks as '', while the grid commits Date objects and
 * object-valued cells (e.g. trade partner). Dates compare by calendar day,
 * objects by identity (id), and the empty markers (null / '') as equal —
 * so unchanged cells don't fire save requests and real edits aren't dropped.
 */
export function isSameCellValue(a, b) {
    if (a instanceof Date || b instanceof Date) {
        return (formatDateToDateString(a) ?? '') === (formatDateToDateString(b) ?? '');
    }
    if (a && b && typeof a === 'object' && typeof b === 'object') {
        return a.id === b.id;
    }
    return (a ?? '') === (b ?? '');
}

/**
 * DataGrid `pastedValueParser` for `type: 'date'` columns.
 * Pasting an empty cell means "clear this date"; pasting a typo must not
 * silently wipe a stored date. The built-in parser collapses both into
 * Invalid Date, so the two intents are separated here:
 *
 * - empty value     -> null (clears the date).
 * - parseable value -> local Date.
 * - anything else   -> ignored (cell keeps previous value).
 */
export const parsePastedDate = (value) => {
    if (value === null || value === undefined || String(value).trim() === '') return null;
    const parsed = parseDateToLocal(value);
    if (!parsed) return undefined;
    // 브라우저 Date 파서는 '111111' 같은 숫자 문자열도 111111년으로 읽어 버린다.
    // 그리드 표시는 연도를 두 자리로 줄여 멀쩡한 날짜처럼 보이지만 DB DATE가 받지
    // 못하는 값이므로(연도 1000~9999), 날짜가 아닌 값과 똑같이 무시한다.
    const year = parsed.getFullYear();
    if (year < 1000 || year > 9999) return undefined;
    return parsed;
};

/**
 * Factory for a DataGrid `pastedValueParser` restricted to a fixed option list.
 * Use on columns typed as `singleSelect`, where the built-in paste parser
 * matches against `valueOptions`.
 *
 * - value in `options` -> pasted as-is.
 * - empty value        -> ignored (cell keeps previous value).
 * - anything else      -> `onInvalid(value)` is called and paste is rejected.
 */
export const createOptionPasteParser = (options, onInvalid) => (value) => {
    if (value === null || value === undefined || value === '') return undefined;
    if (options?.includes(value)) return value;
    onInvalid?.(value);
    return undefined;
};

/**
 * Factory for a DataGrid `valueParser` restricted to a fixed option list.
 * Use on non-`singleSelect` columns (e.g. custom autocomplete/select cells),
 * where clipboard paste flows through `valueParser` instead of `pastedValueParser`.
 *
 * - value in `options` -> committed as-is.
 * - empty value        -> committed as ''.
 * - anything else      -> `onInvalid(value)` is called and the previous value is kept.
 */
export const createOptionValueParser = (field, options, onInvalid) => (value, row) => {
    if (value === null || value === undefined || value === '') return '';
    if (options?.includes(value)) return value;
    onInvalid?.(value);
    return row?.[field] ?? '';
};

/**
 * `valueFormatter` for trade-partner (object-valued) columns.
 * Makes clipboard copy / CSV export emit the partner name as plain text,
 * so it can be pasted into other rows.
 */
export const tradePartnerValueFormatter = (value) =>
    value && typeof value === 'object' ? (value.name ?? '') : (value ?? '');

/** Copy an object-valued cell (trade partner {id,name,iata_firms}, location {id,name,code,country})
 *  as JSON so pasting it elsewhere restores the whole object, not just its displayed text. */
export const objectValueFormatter = (value) => (value && typeof value === 'object' ? JSON.stringify(value) : value ?? '');

/** Exact key set the backend returns for each object-valued type (read.py `il_trade_partner`/
 *  `il_location` search) — used to tell a trade partner apart from a location, since both carry
 *  {id, name} and a subset check alone would let a location paste into a trade partner cell.
 *  Types not listed here only need {id, name} to be present (a saved row carries {id, name},
 *  a freshly picked option {id, name, iata_firms} — both must paste). */
export const REQUIRED_OBJECT_KEYS = {
    AutocompleteFieldTradePartner: ['id', 'name', 'iata_firms'],
    AutocompleteField_CodeName: ['id', 'name', 'code', 'country'],
};

/** Every key a trade partner object can carry: a saved row holds {id, name} (read serializes
 *  relationships that way), a freshly picked option {id, name, iata_firms}. A field without an
 *  exact key set accepts any subset of these that still has id+name — so a location object
 *  ({id, name, code, country}) is rejected by its extra keys, not silently saved as a partner. */
export const TRADE_PARTNER_OBJECT_KEYS = ['id', 'name', 'iata_firms'];

/** A pasted value that clearly came from an object-valued cell (trade partner/location JSON),
 *  landing on a plain text/number/select cell it doesn't belong to. */
export const looksLikeJsonObject = (text) => /^\s*[{[]/.test(text) && /[}\]]\s*$/.test(text.trim());

/** `valueParser`s run on EVERY cell edit commit, not just paste (MUI calls them from
 *  `setEditCellValue` too) — so the parser must pass through values that already come
 *  from this column's own edit component untouched, and only reject truly mismatched pastes.
 *  Returning `undefined` tells the grid to discard the paste and keep the old value.
 *
 *  Trade partner / location cells: our own autocomplete edit components always hand back the
 *  full option object directly (not a string) — only string input (pasted JSON, or pasted plain
 *  text) needs parsing/validation here.
 *
 *  @param {{ key: string, label: string, type?: string }} field
 *  @param {(field) => void} notifyMismatch
 */
export const createObjectValueParser = (field, notifyMismatch) => (value) => {
    if (typeof value !== 'string') return value;
    const trimmed = value.trim();
    if (!trimmed) return '';
    let parsed;
    try {
        parsed = JSON.parse(trimmed);
    } catch {
        notifyMismatch?.(field);
        return undefined;
    }
    const exactKeys = REQUIRED_OBJECT_KEYS[field.type];
    const requiredKeys = exactKeys || ['id', 'name'];
    const allowedKeys = exactKeys || TRADE_PARTNER_OBJECT_KEYS;
    const hasValidShape =
        parsed &&
        typeof parsed === 'object' &&
        !Array.isArray(parsed) &&
        requiredKeys.every((key) => key in parsed) &&
        Object.keys(parsed).every((key) => allowedKeys.includes(key));
    if (!hasValidShape) {
        notifyMismatch?.(field);
        return undefined;
    }
    return parsed;
};

/**
 * `filterOperators` for object-valued columns (trade partner, location): filters by the
 * given property, since the built-in string operators can't reach into an object-valued
 * cell. Defaults to `.name`; pass `'code'` for columns that display the location code
 * instead (e.g. AOL/AOD/FAOD show `value.code`, not `value.name`).
 */
export const createNameFilterOperators = (key = 'name') => [
    {
        label: 'contains',
        value: 'contains',
        InputComponent: GridFilterInputValue,
        getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
                return null;
            }
            const lowerCaseFilterValue = filterItem.value.toLowerCase();
            return (value) => value?.[key]?.toString().toLowerCase().includes(lowerCaseFilterValue);
        },
    },
    {
        label: 'does not contain',
        value: 'doesNotContain',
        InputComponent: GridFilterInputValue,
        getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
                return null;
            }
            const lowerCaseFilterValue = filterItem.value.toLowerCase();
            return (value) => !value?.[key]?.toString().toLowerCase().includes(lowerCaseFilterValue);
        },
    },
];

/**
 * Factory for a DataGrid `valueParser` for trade-partner (object-valued) columns.
 * Protects against clipboard paste corrupting the cell, while still supporting
 * row-to-row copy/paste inside the same grid:
 * - object value (from the autocomplete editor) -> kept as-is.
 * - empty value                                 -> previous value kept.
 * - string matching a partner name already loaded in the grid rows or in the
 *   current search options                       -> resolved to that partner object.
 * - anything else                               -> previous value kept.
 *
 * @param {string} field   - the column field key
 * @param {Array}  options - the currently loaded trade-partner options ([{ id, name }])
 */
export const createTradePartnerValueParser = (field, options) => (value, row, column, apiRef) => {
    if (value && typeof value === 'object') return value;
    if (value === null || value === undefined || value === '') return row?.[field] ?? '';

    const candidates = [...(options || [])];
    try {
        apiRef?.current?.getRowModels?.().forEach((rowModel) => {
            const partner = rowModel?.[field];
            if (partner && typeof partner === 'object') candidates.push(partner);
        });
    } catch {
        // apiRef not available (e.g. non-clipboard edit path) -> fall back to options only.
    }

    const match = candidates.find((option) => option?.name === value);
    return match ?? row?.[field] ?? '';
};

/**
 * Factory for a DataGrid `valueParser` for string fields whose valid values come from
 * a dynamically-fetched `{ id, name }` option list (e.g. server-searched autocomplete),
 * rather than a fixed list. Since the loaded `options` only reflect the current search
 * term, valid values already present in sibling rows of the same grid are also accepted
 * so a paste isn't rejected just because that name hasn't been searched for yet.
 *
 * - empty value                                  -> previous value kept.
 * - name matching a loaded option or another row's value in this field -> committed as-is.
 * - anything else                                -> `onInvalid(value)` is called and the previous value is kept.
 *
 * @param {string} field   - the column field key
 * @param {Array}  options - the currently loaded `{ id, name }` options
 */
export const createOptionNameValueParser = (field, options, onInvalid) => (value, row, column, apiRef) => {
    if (value === null || value === undefined || value === '') return row?.[field] ?? '';

    const candidateNames = new Set((options || []).map((option) => option?.name).filter(Boolean));
    try {
        apiRef?.current?.getRowModels?.().forEach((rowModel) => {
            const existing = rowModel?.[field];
            if (existing) candidateNames.add(existing);
        });
    } catch {
        // apiRef not available (e.g. non-clipboard edit path) -> fall back to options only.
    }

    if (candidateNames.has(value)) return value;
    onInvalid?.(value);
    return row?.[field] ?? '';
};

/**
 * Factory for a "fill down" handler: copies the first row's value in a field
 * to every row below it, persisting each change via `handleProcessRowUpdate`.
 *
 * Rows are saved one at a time, each waiting for the previous save's response.
 * Firing them together looked faster but piled up one in-flight request (and one
 * DB connection) per row, all queued behind the same shipment lock the server
 * takes to recalculate todos — a container-heavy shipment could exhaust the
 * connection pool. Sequential saves keep one request in flight, so the lock is
 * never contended and the queue never forms.
 *
 * A row whose save is rejected does not stop the rows after it: the parallel
 * version attempted every row, and a single bad cell (e.g. a value the server
 * refuses) must not silently drop the rest of the copy.
 *
 * @param {object}   cfg
 * @param {Function} cfg.dispatch          - redux dispatch
 * @param {boolean}  cfg.disabled          - when true, the handler is a no-op (e.g. closed case)
 * @param {Array}    cfg.rows              - the rows currently displayed in the grid (in display order)
 * @param {number}   cfg.tabValue          - shipment tab value
 * @param {string}   cfg.methodTableName   - socket table name
 * @param {string}   cfg.reducerName       - redux reducer name
 * @param {Function} cfg.getTableId        - (row) => tableId array for the socket update
 * @param {Array}    cfg.formData          - full redux form data used to diff old vs new row
 * @param {Array}    cfg.notAllowNullKey   - keys that may not be null
 * @param {Array}    cfg.dateField         - keys treated as dates
 * @returns {(field: string) => Promise<void>}
 */
export const createFillDownHandler =
    ({ dispatch, disabled, rows, tabValue, methodTableName, reducerName, getTableId, formData, notAllowNullKey, dateField }) =>
    async (field) => {
        if (disabled) return;
        if (!Array.isArray(rows) || rows.length < 2) return;

        const sourceValue = rows[0][field];

        for (const row of rows.slice(1)) {
            if (JSON.stringify(row[field]) === JSON.stringify(sourceValue)) continue;
            try {
                await handleProcessRowUpdate({
                    dispatch,
                    tabValue,
                    methodTableName,
                    reducerName,
                    tableId: getTableId(row),
                    formData,
                    newRow: { ...row, [field]: sourceValue },
                    notAllowNullKey,
                    dateField,
                });
            } catch {
                // A rejected row must not stop the rows below it, and the reason for the
                // rejection has already been surfaced to the user by handleProcessRowUpdate.
            }
        }
    };
